import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { LayoutTemplate, Pencil, Trash2, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { gradientCss } from "@/lib/gradients";
import { resolveMediaUrl } from "@/components/FileUpload";

const thumbStyle = (tpl) => (tpl.media_url
  ? { backgroundImage: `url(${resolveMediaUrl(tpl.media_url)})`, backgroundSize: "cover", backgroundPosition: "center" }
  : tpl.bg && tpl.bg.startsWith("/verse-bg/") ? { backgroundImage: `url(${tpl.bg})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { background: gradientCss(tpl.bg) || "var(--yalola-primary)" });

// Page Église : modèles de story enregistrés (renommer / supprimer). Création depuis l'éditeur téléphone (⋯ → Enregistrer comme modèle).
export const ChurchStoryTemplates = ({ churchId, t }) => {
  const [templates, setTemplates] = useState(null);
  const [editing, setEditing] = useState(null); // { id, name }
  useEffect(() => {
    if (!churchId) return;
    api.get(`/churches/${churchId}/story-templates`).then(({ data }) => setTemplates(data?.templates || [])).catch(() => setTemplates([]));
  }, [churchId]);
  if (templates === null) return null;

  const rename = async () => {
    const name = (editing?.name || "").trim();
    if (!name) return;
    try {
      const { data } = await api.patch(`/churches/${churchId}/story-templates/${editing.id}`, { name });
      setTemplates((arr) => arr.map((x) => (x.id === editing.id ? { ...x, ...data.template } : x)));
      setEditing(null);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };
  const remove = async (tpl) => {
    if (!window.confirm(t("story.tpl_delete_confirm", "Supprimer ce modèle ?"))) return;
    try {
      await api.delete(`/churches/${churchId}/story-templates/${tpl.id}`);
      setTemplates((arr) => arr.filter((x) => x.id !== tpl.id));
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };

  return (
    <div className="mb-4 rounded-xl border p-3" style={{ borderColor: "var(--yalola-border)", background: "rgba(0,0,0,0.02)" }} data-testid="church-story-templates">
      <div className="flex items-center gap-2 mb-1">
        <LayoutTemplate size={15} style={{ color: "var(--yalola-accent)" }} />
        <span className="text-sm font-semibold">{t("church.story_templates_title", "Modèles de story")} ({templates.length}/12)</span>
      </div>
      <p className="text-[11px] mb-2" style={{ color: "var(--yalola-text-muted)" }}>{t("church.story_templates_hint", "Crée un modèle depuis l'éditeur de story sur téléphone : menu ⋯ → « Enregistrer comme modèle de l'église ».")}</p>
      {templates.length === 0 ? (
        <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="church-story-templates-empty">{t("church.no_story_templates", "Aucun modèle pour l'instant.")}</p>
      ) : (
        <div className="space-y-2">
          {templates.map((tpl) => (
            <div key={tpl.id} className="flex items-center gap-3 rounded-lg bg-white border px-3 py-2" style={{ borderColor: "var(--yalola-border)" }} data-testid={`church-story-template-${tpl.id}`}>
              <div className="shrink-0 h-11 w-8 rounded-md border" style={{ borderColor: "var(--yalola-border)", ...thumbStyle(tpl) }} />
              {editing?.id === tpl.id ? (
                <>
                  <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value.slice(0, 60) })} onKeyDown={(e) => { if (e.key === "Enter") rename(); if (e.key === "Escape") setEditing(null); }} autoFocus className="flex-1 min-w-0 rounded-md border px-2 py-1 text-sm outline-none" style={{ borderColor: "var(--yalola-border)" }} data-testid={`church-story-template-rename-input-${tpl.id}`} />
                  <button type="button" onClick={rename} className="h-8 w-8 grid place-items-center rounded-full text-white" style={{ background: "var(--yalola-primary)" }} aria-label={t("common.save", "Enregistrer")} data-testid={`church-story-template-rename-save-${tpl.id}`}><Check size={14} /></button>
                  <button type="button" onClick={() => setEditing(null)} className="h-8 w-8 grid place-items-center rounded-full border" style={{ borderColor: "var(--yalola-border)" }} aria-label={t("common.cancel", "Annuler")}><X size={14} /></button>
                </>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate" data-testid={`church-story-template-name-${tpl.id}`}>{tpl.name}</div>
                    <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{(tpl.stickers || []).length} {t("story.tool_stickers", "Stickers").toLowerCase()}{tpl.drawing_url ? ` · ${t("story.tool_draw", "Dessin").toLowerCase()}` : ""}{tpl.media_url ? ` · ${t("common.photo", "Photo").toLowerCase()}` : ""}</div>
                  </div>
                  <button type="button" onClick={() => setEditing({ id: tpl.id, name: tpl.name })} className="h-8 w-8 grid place-items-center rounded-full border" style={{ borderColor: "var(--yalola-border)" }} aria-label={t("common.rename", "Renommer")} data-testid={`church-story-template-rename-${tpl.id}`}><Pencil size={14} /></button>
                  <button type="button" onClick={() => remove(tpl)} className="h-8 w-8 grid place-items-center rounded-full border text-red-600" style={{ borderColor: "var(--yalola-border)" }} aria-label={t("common.delete", "Supprimer")} data-testid={`church-story-template-delete-${tpl.id}`}><Trash2 size={14} /></button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
